/** Theme catalogue + pure helpers shared by the Theme Picker (tiles, shuffle, presets). */
import { PALETTES, FONT_PAIRS } from "@/lib/design";

export const HEADERS = [
  { value: "minimal", label: "Minimal", tagline: "Just the wordmark + nav." },
  { value: "downtown", label: "Downtown", tagline: "Compact news-row layout." },
  { value: "urban-centered", label: "Urban Centered", tagline: "Centered brand block." },
  { value: "news-magazine", label: "News Magazine", tagline: "Full-width tabloid header." },
  { value: "split-magazine", label: "Split Magazine", tagline: "White logo band + blue rail." },
  { value: "classic-serif", label: "Classic Serif", tagline: "Utility strip + big centered serif wordmark + rail." },
  { value: "bold-sans-left", label: "Bold Sans Left", tagline: "Logo left + inline nav + socials right." },
  { value: "stacked-editorial", label: "Stacked Editorial", tagline: "NYT-style: big center wordmark + pipe nav." },
  { value: "compact-utility", label: "Compact Utility", tagline: "Utility bar + logo + nav + subscribe CTA." },
  { value: "magazine-classic", label: "Magazine Classic", tagline: "Rules flank wordmark + accent stripe nav." },
];

export const FOOTERS = [
  { value: "minimal", label: "Minimal", tagline: "Single-row attribution." },
  { value: "navy-brand", label: "Navy Brand", tagline: "Bold brand-forward column." },
  { value: "dark-editorial", label: "Dark Editorial", tagline: "Multi-col link rail." },
  { value: "centered-magazine", label: "Centered Magazine", tagline: "Editorial credits." },
  { value: "split-magazine", label: "Split Magazine", tagline: "Subscribe band + 5-col grid." },
  { value: "compact-cols", label: "Compact Cols", tagline: "Clean 3-col brand/sections/legal." },
  { value: "mono-centered", label: "Mono Centered", tagline: "Centered mono attribution + pipe nav." },
  { value: "editorial-grid", label: "Editorial Grid", tagline: "Asymmetric brand block + 3-col rail." },
  { value: "stripe-band", label: "Stripe Band", tagline: "Accent stripe + logo/nav/social row." },
  { value: "big-newsletter", label: "Big Newsletter", tagline: "Hero subscribe box + thin legal row." },
];

export const LAYOUTS = [
  { template: "tribune", variant: "v1", label: "Tribune · Broadsheet", tagline: "5-col dense grid, no hero." },
  { template: "tribune", variant: "v2", label: "Tribune · Magazine", tagline: "Massive hero + 3-up grid." },
  { template: "tribune", variant: "v3", label: "Tribune · Wire", tagline: "Ticker + 2-col lead." },
  { template: "terminal", variant: "v1", label: "Terminal · Mainframe", tagline: "Mono headlines, ticker rail." },
  { template: "terminal", variant: "v2", label: "Terminal · Trading Floor", tagline: "Tables + data tiles." },
  { template: "terminal", variant: "v3", label: "Terminal · Quiet Mono", tagline: "Minimal mono, single column." },
  { template: "brief", variant: "v1", label: "Brief · Editorial", tagline: "Large hero + pull quotes." },
  { template: "brief", variant: "v2", label: "Brief · Curated", tagline: "Asymmetric, picks of week." },
  { template: "brief", variant: "v3", label: "Brief · Whitespace", tagline: "Minimal, single-feature." },
  { template: "newsmag", variant: "v1", label: "Newsmag · Tech", tagline: "Auto-rotating hero, magazine rows, image-rich." },
  { template: "newsmag", variant: "v2", label: "Newsmag · Urban Observer", tagline: "2-col main+sidebar, Top 5 numbered widget." },
  { template: "newsmag", variant: "v3", label: "Newsmag · Free News", tagline: "2×2 per-category modules, hot topics ribbon." },
];

const PALETTE_TAGLINES = {
  "newsprint": "Warm off-white, oxblood accent.",
  "amber-noir": "Dark mode, amber accent.",
  "oxblood": "Cream + deep oxblood.",
  "sage": "Calm green-tinted whites.",
  "indigo": "Dark indigo, periwinkle accent.",
};
export const PALETTE_ITEMS = Object.entries(PALETTES).map(([key, p]) => ({
  value: key, label: p.label, tagline: PALETTE_TAGLINES[key] || "Custom palette.",
  swatch: { bg: p.bg, surface: p.surface, accent: p.accent, text: p.text },
}));

export const FONT_ITEMS = Object.entries(FONT_PAIRS).map(([key, fp]) => ({
  value: key, label: fp.label,
  tagline: `${fp.display.split(",")[0].replace(/'/g, "")} + ${fp.body.split(",")[0].replace(/'/g, "")}`,
}));

export const TABS = [
  { id: "header", label: "Header", items: HEADERS, kind: "header" },
  { id: "footer", label: "Footer", items: FOOTERS, kind: "footer" },
  { id: "layout", label: "Layout", items: LAYOUTS, kind: "layout" },
  { id: "palette", label: "Palette", items: PALETTE_ITEMS, kind: "palette" },
  { id: "font", label: "Font", items: FONT_ITEMS, kind: "font" },
  { id: "shuffle", label: "Shuffle", items: [], kind: "shuffle" },
  { id: "presets", label: "Presets", items: [], kind: "presets" },
];

export const DIMENSIONS = ["header", "footer", "layout", "palette", "font"];

/** Stable identity of an item ("template/variant" for layouts, `value` otherwise). */
export const itemKey = (item) => item.value ?? `${item.template}/${item.variant}`;

/** Site PATCH body that applies `item` for one dimension. */
export function patchFor(kind, item) {
  if (kind === "header") return { header_variant: item.value };
  if (kind === "footer") return { footer_variant: item.value };
  if (kind === "palette") return { palette: item.value };
  if (kind === "font") return { font_pair: item.value };
  return { template: item.template, variant: item.variant };
}

/** `?preview_*=` params that vary ONLY this dimension. */
export function previewParamsFor(kind, item) {
  if (kind === "header") return { preview_header: item.value };
  if (kind === "footer") return { preview_footer: item.value };
  if (kind === "palette") return { preview_palette: item.value };
  if (kind === "font") return { preview_font: item.value };
  return { preview_template: item.template, preview_variant: item.variant };
}

/** The site's CURRENT persisted value for each dimension, resolved to a catalogue item. */
export function currentItems(site) {
  return {
    header: HEADERS.find((h) => h.value === site.header_variant) || HEADERS[0],
    footer: FOOTERS.find((f) => f.value === site.footer_variant) || FOOTERS[0],
    layout: LAYOUTS.find((l) => l.template === site.template && l.variant === site.variant) || LAYOUTS[0],
    palette: PALETTE_ITEMS.find((p) => p.value === site.palette) || PALETTE_ITEMS[0],
    font: FONT_ITEMS.find((f) => f.value === site.font_pair) || FONT_ITEMS[0],
  };
}

const SITE_FIELD = { header: "header_variant", footer: "footer_variant", palette: "palette", font: "font_pair" };

export function isActive(site, kind, item) {
  if (kind === "layout") return site.template === item.template && site.variant === item.variant;
  return site[SITE_FIELD[kind]] === item.value;
}

export function previewUrl(site, params, bust) {
  const p = new URLSearchParams({ ...params, _b: bust });
  return `${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${site.slug}?${p.toString()}`;
}
